import React, {PureComponent} from "react"
import {Col} from "react-bootstrap"
import {withRouter, RouteComponentProps} from "react-router-dom"
import qs from "qs"

import id from "@irati/utils/id"
import {H1} from "@irati/ui/src/Typography"
import Form, {FormGroup} from "@irati/ui/src/Form"
import Button from "@irati/ui/src/Button"
import {Input} from "@irati/ui/src/FormItem"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import {Password} from "@irati-web/fields/user"
import HR from "@irati/ui/src/Layout/Hr"
import Validation from "@irati/utils/validation"
import authentication from "@irati/auth"
import Loading from "@irati/ui/src/Loading"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import loginService from "@irati/auth/services/login"
import Link from "@irati/ui/src/Link"
import profileIcon from "@irati/ui/images/profile.svg"
import {initiateSSOLogin} from "@irati/sso/helpers/handleSSOFlow"
import ssoErrorHandler from "@irati/sso/services/errors/ssoErrorHandler"
import ErrorText from "@irati/ui/src/ErrorText"
import Spacing from "@irati/ui/src/Spacing"
import {getAuthURL} from "@irati/sso/services/sso"

import {AuthUrlState} from "@irati/types/ops"
import links from "@irati/utils/links"
import LoginBlock from "../../../components/LoginBlock"

import ForgotButton from "../styled/ForgotButton"
import StyledCheckbox from "../styled/StyledCheckbox"

interface Props extends InjectIntl, RouteComponentProps {
  id: string,
  postedEmail: string,
  onContinue: () => void
}

interface State {
  email: string,
  password: string,
  load: Promise<any> | null,
  submit: Promise<any> | null,
  rememberMe: boolean,
  ssoError: string | null,
  ssoURL: string | null,
  useRedirect: boolean
}

class FullLogin extends PureComponent<Props, State> {
  static defaultProps = {
    postedEmail: null
  }

  state: State = {
    email: "",
    password: "",
    rememberMe: true,
    load: null,
    submit: null,
    ssoError: null,
    ssoURL: null,
    useRedirect: false
  }

  componentDidMount() {
    const {postedEmail} = this.props
    if (postedEmail) {
      this.setState({email: postedEmail})
    }

    const state: AuthUrlState = {}
    const nextPathname = this.props.location?.state?.nextPathname || ""
    const search = nextPathname.split("?")[1]
    if (search) {
      const obj = qs.parse(search)
      if (obj.useRedirectSso) {
        this.setState({useRedirect: true})
        const query = qs.stringify({nextPathname})
        state.url = `${links.getDomain(authentication.unAuthedBusiness().subdomain)}/welcome/login/idp?${query}`
      }
    }

    const load = getAuthURL(authentication.unAuthedBusiness().uuid!, undefined, state)
      .then(({url}) => {
        this.setState({ssoURL: url!})
      })
      .catch((err) => {
        const ssoError = ssoErrorHandler(err)
        this.setState({ssoError})
      })

    this.setState({load})
  }

  handleSubmit = () => {
    const {onContinue} = this.props
    const {email, password} = this.state
    const business = authentication.unAuthedBusiness()

    const submit = loginService.login(business, email, password).then(onContinue)
    this.setState({submit})
  }

  handleChangeEmail = (email: string) => this.setState({email, load: null})

  handleChangePassword = (password: string) => this.setState({password, load: null})

  updateRememberMe = () => this.setState({rememberMe: !this.state.rememberMe})

  handleEnter = () =>
    Validation.validate()
      .then(this.handleSubmit)
      .catch(() => {})

  emailValidation = () => {
    const schema = Validation.string("platform.defaultLogin.fields.emailValidation.enterEmail")
    return schema.email("platform.defaultLogin.fields.emailValidation.notValid")
  }

  singleSignOn = () => {
    const {onContinue} = this.props
    this.setState({ssoError: null})
    const {useRedirect} = this.state

    let submit: Promise<any> = initiateSSOLogin(this.state.ssoURL!, {useRedirect})
      .then((response) => {
        const {accessToken} = response
        submit = loginService.ssoLogin(accessToken).then(onContinue)
        this.setState({submit})
        return submit
      })
      .catch((err) => {
        const ssoError = ssoErrorHandler(err)
        this.setState({ssoError})
      })

    if (useRedirect) {
      this.setState({submit})
    }
  }

  render() {
    const {formatMessage, id: idNamespace} = this.props
    const {email, password, rememberMe, submit, ssoError, load, ssoURL} = this.state
    const {businessName, showSsoLogin, showPasswordLogin, loginPageSsoButtonText} = authentication.unAuthedBusiness()

    const setupLink = (
      <FormattedMessage
        id="platform.defaultLogin.tenantLogin.help.link.body"
        values={{
          link: (
            <Link href={formatMessage({id: "platform.defaultLogin.tenantLogin.help.link.url"})} target="_blank">
              <FormattedMessage id="platform.defaultLogin.tenantLogin.help.link.text" />
            </Link>
          )
        }} />
    )
    const helpLinks = showSsoLogin
      ? [
        <FormattedMessage
          id="platform.defaultLogin.tenantLogin.ssoHelp.link.body"
          values={{
            link: (
              <Link href={formatMessage({id: "platform.defaultLogin.tenantLogin.ssoHelp.link.url"})} target="_blank">
                <FormattedMessage id="platform.defaultLogin.tenantLogin.ssoHelp.link.text" />
              </Link>
            )
          }} />,
        setupLink
      ]
      : [setupLink]

    return (
      <div id={idNamespace} className="default_login-container">
        <Loading load={load}>
          <LoginBlock id={idNamespace} image={profileIcon} helpLinks={helpLinks}>
            <H1 marginBottom={4}>
              <FormattedMessage id="platform.defaultLogin.tenantLogin.header" values={{businessName}} />
            </H1>
            {ssoURL && (
              <div>
                <Button primary full large id={id("btn-workOS-sso", idNamespace)} load={submit} onClick={this.singleSignOn}>
                  {loginPageSsoButtonText || <FormattedMessage id="platform.defaultLogin.tenantLogin.button.singleSignOn" />}
                </Button>
                {ssoError && (
                  <Spacing marginTop={2}>
                    <ErrorText>{formatMessage({id: ssoError})}</ErrorText>
                  </Spacing>
                )}
                {showPasswordLogin && <HR margin={[4, 0]} />}
              </div>
            )}
            {/* include emergency fallback in case a tenant has showSsoLogin false && showPasswordLogin false */}
            {(showPasswordLogin || (!showPasswordLogin && !showSsoLogin && !ssoURL)) && (
              <Form>
                <Input
                  id={id("field-email", idNamespace)}
                  label={formatMessage({id: "platform.defaultLogin.fields.email.label"})}
                  name="email"
                  type="email"
                  value={email}
                  placeholder={formatMessage({id: "platform.defaultLogin.fields.email.placeholder"})}
                  onChange={this.handleChangeEmail}
                  onEnter={this.handleEnter}
                  validation={this.emailValidation()} />
                <Password
                  id={id("field-password", idNamespace)}
                  label={formatMessage({id: "platform.defaultLogin.fields.password.label"})}
                  value={password}
                  noValidation
                  isRequired
                  placeholder={formatMessage({id: "platform.defaultLogin.fields.password.placeholder"})}
                  onChange={this.handleChangePassword}
                  onEnter={this.handleEnter} />
                <FormGroup>
                  <Col xs={12}>
                    <ForgotButton id={id("btn-forgot", idNamespace)} to="/welcome/login/forgotten-password">
                      <FormattedMessage id="platform.defaultLogin.fields.button.forgotPassword" />?
                    </ForgotButton>
                  </Col>
                </FormGroup>
                <StyledCheckbox
                  id={id("field-remember-me", idNamespace)}
                  name="rememberMe"
                  checked={rememberMe}
                  onChange={this.updateRememberMe}>
                  <FormattedMessage id="platform.defaultLogin.fields.rememberMe.label" />
                </StyledCheckbox>
                <Loading id={id("submit-response", idNamespace)} load={submit} useLoader={false} />
                <ValidationButton
                  primary={!showSsoLogin}
                  large
                  full
                  id={id("btn-submit", idNamespace)}
                  load={submit}
                  onClick={this.handleSubmit}>
                  <FormattedMessage
                    id={showSsoLogin ? "platform.defaultLogin.fields.button.loginEmail" : "platform.defaultLogin.fields.button.login"} />
                </ValidationButton>
              </Form>
            )}
          </LoginBlock>
        </Loading>
      </div>
    )
  }
}

export default injectIntl(withRouter(FullLogin))
